/**
 * DistributionEntityFilter - Filtro de tipo de entidade para dashboard de distribuição
 * 
 * Permite filtrar métricas e distribuições por:
 * - Todas as entidades
 * - Apenas conversas
 * - Apenas deals (CRM)
 * 
 * Design premium com tabs estilizados e contadores
 * 
 * @version 1.0.0
 * @date 2026-01-20
 */
import { MessageSquare, Briefcase, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Tipo de entidade - definido localmente para evitar problemas de cache de tipos
type EntityType = 'conversation' | 'deal' | 'all';
// ==============================================================
// Interfaces
// ==============================================================

interface DistributionEntityFilterProps {
  /** Valor atual do filtro */
  value: EntityType;
  /** Callback quando o filtro muda */
  onChange: (value: EntityType) => void;
  /** Contadores por tipo */
  counts: {
    all: number;
    conversation: number;
    deal: number;
  };
  /** Indica se está carregando */
  isLoading?: boolean;
}

// ==============================================================
// Componente Principal
// ==============================================================

export function DistributionEntityFilter({ 
  value, 
  onChange, 
  counts, 
  isLoading 
}: DistributionEntityFilterProps) {
  // Configuração dos botões de filtro
  const filterOptions: Array<{
    key: EntityType;
    label: string;
    icon: typeof MessageSquare;
    color: string;
  }> = [
    { key: 'all', label: 'Todos', icon: Layers, color: 'text-foreground' },
    { key: 'conversation', label: 'Conversas', icon: MessageSquare, color: 'text-emerald-600' },
    { key: 'deal', label: 'Negócios', icon: Briefcase, color: 'text-blue-600' },
  ];

  return (
    <div className="flex gap-1.5 p-1 bg-muted/50 rounded-lg w-fit">
      {filterOptions.map(option => {
        const Icon = option.icon;
        const isActive = value === option.key;
        const count = counts[option.key];
        
        return (
          <Button
            key={option.key}
            variant={isActive ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onChange(option.key)}
            disabled={isLoading}
            className={cn(
              'gap-1.5 h-8 px-3 transition-all duration-200',
              isActive && 'shadow-sm',
              !isActive && 'hover:bg-muted'
            )}
          >
            <Icon className={cn(
              'h-3.5 w-3.5',
              isActive ? 'text-primary-foreground' : option.color
            )} />
            <span className="text-xs font-medium">
              {option.label}
            </span>
            <span className={cn(
              'text-[10px] px-1.5 py-0.5 rounded-full min-w-[20px] text-center',
              isActive 
                ? 'bg-primary-foreground/20 text-primary-foreground' 
                : 'bg-muted text-muted-foreground'
            )}>
              {isLoading ? '-' : count}
            </span>
          </Button>
        );
      })}
    </div>
  );
}
